import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { FilterControl } from './FilterControls';
import { useFilterPanelFloatingMenu } from './filterDropdownShared';

export type AssigneePeerOption = {
  user_id: number;
  user_name: string;
  user_email?: string | null;
};

type FilterAssigneeComboboxProps = {
  id: string;
  peerOptions: AssigneePeerOption[];
  filteredPeers: AssigneePeerOption[];
  searchText: string;
  onSearchTextChange: (value: string) => void;
  selectedPeerId: number | null;
  onSelectPeer: (peer: AssigneePeerOption) => void;
  onClearPeer: () => void;
};

const FilterAssigneeCombobox: React.FC<FilterAssigneeComboboxProps> = ({
  id,
  peerOptions,
  filteredPeers,
  searchText,
  onSearchTextChange,
  selectedPeerId,
  onSelectPeer,
  onClearPeer,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const listboxId = `${id}-listbox`;

  const showMenu = open && filteredPeers.length > 0;
  const menuLayout = useFilterPanelFloatingMenu(
    showMenu,
    filteredPeers.length,
    { wrapRef, triggerRef, menuRef },
    () => setOpen(false),
  );

  const openSuggestions = () => {
    if (peerOptions.length > 0) setOpen(true);
  };

  const menu =
    showMenu && menuLayout ? (
      <ul
        ref={menuRef}
        id={listboxId}
        role="listbox"
        className="filter-panel__select-menu filter-panel__select-menu--floating filter-panel__assignee-menu-list"
        style={{
          top: menuLayout.top,
          left: menuLayout.left,
          width: menuLayout.width,
          height: menuLayout.height,
        }}
      >
        {filteredPeers.map((peer) => (
          <li
            key={peer.user_id}
            role="option"
            aria-selected={selectedPeerId === peer.user_id}
            className={`filter-panel__select-option filter-panel__assignee-option${
              selectedPeerId === peer.user_id ? ' filter-panel__select-option--selected' : ''
            }`}
            onMouseDown={(event) => {
              event.preventDefault();
              onSelectPeer(peer);
              setOpen(false);
            }}
          >
            <span className="fw-medium d-block text-truncate">{peer.user_name}</span>
            {peer.user_email ? (
              <span className="text-muted d-block text-truncate filter-panel__assignee-option-email">
                {peer.user_email}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    ) : null;

  return (
    <div className={`filter-panel__combobox${open ? ' filter-panel__combobox--open' : ''}`} ref={wrapRef}>
      <FilterControl
        id={id}
        ref={triggerRef}
        type="text"
        role="combobox"
        aria-expanded={showMenu}
        aria-controls={listboxId}
        aria-autocomplete="list"
        className={`filter-panel__assignee-input${
          selectedPeerId != null ? ' filter-panel__assignee-input--with-clear' : ''
        }`}
        autoComplete="off"
        placeholder={peerOptions.length === 0 ? '—' : 'Selecionar…'}
        value={searchText}
        onChange={(event) => {
          onSearchTextChange(event.target.value);
          openSuggestions();
        }}
        onFocus={openSuggestions}
        onClick={openSuggestions}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
          if (event.key === 'Enter' && filteredPeers.length === 1) {
            event.preventDefault();
            onSelectPeer(filteredPeers[0]);
            setOpen(false);
          }
        }}
      />
      <div
        className={
          'filter-panel__assignee-suffix' +
          (selectedPeerId != null ? ' filter-panel__assignee-suffix--with-clear' : '')
        }
      >
        {selectedPeerId != null ? (
          <button
            type="button"
            className="filter-panel__assignee-clear"
            onClick={(event) => {
              event.stopPropagation();
              onClearPeer();
            }}
            title="Limpar"
          >
            ×
          </button>
        ) : null}
        <span className="filter-panel__assignee-caret" aria-hidden>
          ▼
        </span>
      </div>
      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
};

export default FilterAssigneeCombobox;
